import React, { forwardRef } from 'react';
import type { ComponentProps } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';

import {
	GRADIENT_CTA_BUTTON_INVERTED_BORDER_START,
	GRADIENT_CTA_BUTTON_INVERTED_BORDER_END,
	GRADIENT_CTA_BUTTON_ACTIVE_START,
	GRADIENT_CTA_BUTTON_ACTIVE_END,
	GRADIENT_CTA_BUTTON_SELECTED_START,
	GRADIENT_CTA_BUTTON_SELECTED_END,
} from './GradientConstants';
import { ButtonBase } from './GradientCTAButtonBase';

type ButtonTextProps = {
	textMinWidth?: string;
	textMaxWidth?: string;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(ButtonBase)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		height: '30px',
		borderRadius: `${
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
			token('border.radius.100', '3px')
		}`,
		transition: 'all 0.3s ease-in-out 0s',
		backgroundColor: `${token('color.text.inverse')}`,
		padding: `${token('space.075')} ${token('space.150')}`,
		color: `${token('color.background.accent.purple.bolder')}`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			margin: '0px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'> span:first-of-type': {
			color: `${token('color.background.accent.purple.bolder')}`,
		},

		'&:hover': {
			cursor: 'pointer',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& span': {
				color: `${token('color.text.inverse')}`,
			},

			backgroundImage: `linear-gradient(45deg,
      ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_ACTIVE_START} 0%,
      ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_ACTIVE_END} 100%)`,
		},
		'&:active': {
			cursor: 'pointer',
			color: `${token('color.text.inverse')}`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& span': {
				color: `${token('color.text.inverse')}`,
			},

			backgroundImage: `linear-gradient(45deg,
      ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_SELECTED_START} 0%,
      ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_SELECTED_END} 100%)`,
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonText = styled.span<ButtonTextProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: (props) => props.textMinWidth,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: (props) => props.textMaxWidth,
	font: token('font.body'),
	display: 'block',
	textAlign: 'center',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundImage: `linear-gradient( 45deg, ${GRADIENT_CTA_BUTTON_INVERTED_BORDER_START} 0%, ${GRADIENT_CTA_BUTTON_INVERTED_BORDER_END} 100% )`,
	WebkitBackgroundClip: 'text',
	backgroundClip: 'text',
	color: 'transparent',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GradientBorderBox = styled.div({
	// the padding below is served as border, so border width token is used.
	padding: token('border.width'),
	display: 'inline-flex',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('border.radius.100', '3px'),

	backgroundImage: `linear-gradient(45deg,
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
        ${GRADIENT_CTA_BUTTON_INVERTED_BORDER_START} 0%,
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
        ${GRADIENT_CTA_BUTTON_INVERTED_BORDER_END} 100%)`,
	'&:hover': {
		backgroundImage: `linear-gradient(45deg,
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_ACTIVE_START} 0%,
  ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_ACTIVE_END} 100%)`,
	},
	'&:active': {
		backgroundImage: `linear-gradient(45deg,
    ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_SELECTED_START} 0%,
    ${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766 */ ''}
              ${GRADIENT_CTA_BUTTON_SELECTED_END} 100%)`,
	},
});

/**
 * A styled button with a gradient border, intended to be used as a product edition upgrade CTA button.
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const GradientCTAInvertedButton = forwardRef<
	HTMLButtonElement,
	ComponentProps<typeof StyledButton> & ButtonTextProps
>(({ textMaxWidth, textMinWidth, children, ...buttonProps }, ref) => {
	return (
		<GradientBorderBox>
			<StyledButton iconBefore={<EditionsIcon label="" />} {...buttonProps} ref={ref}>
				<ButtonText textMaxWidth={textMaxWidth} textMinWidth={textMinWidth}>
					{children}
				</ButtonText>
			</StyledButton>
		</GradientBorderBox>
	);
});
